import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Plus, Trash2, Save, Database } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["docx", "xlsx", "pdf"];

export default function CatalogPage() {
  const [cat, setCat] = useState(null);
  const [edit, setEdit] = useState({});

  const load = () => api.get("/catalog").then(({ data }) => setCat(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const seed = async () => { try { await api.post("/catalog/seed"); toast.success("Alıcı kataloğu yüklendi."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const startEdit = (a) => setEdit({ ...edit, [a.activity_code]: a.docs.map((d) => ({ ...d })) });
  const addDoc = (act) => setEdit({ ...edit, [act]: [...(edit[act] || []), { key: `doc_${Date.now()}`, name: "", working_types: ["docx"] }] });
  const rmDoc = (act, i) => setEdit({ ...edit, [act]: edit[act].filter((_, x) => x !== i) });
  const setName = (act, i, v) => setEdit({ ...edit, [act]: edit[act].map((d, x) => x === i ? { ...d, name: v } : d) });
  const toggleType = (act, i, t) => setEdit({ ...edit, [act]: edit[act].map((d, x) => x === i ? { ...d, working_types: d.working_types.includes(t) ? d.working_types.filter((y) => y !== t) : [...d.working_types, t] } : d) });

  const save = async (act) => {
    try {
      const docs = (edit[act] || []).filter((d) => d.name.trim());
      await api.put(`/catalog/${act}`, { docs });
      toast.success("Katalog kaydedildi.");
      setEdit((e) => { const c = { ...e }; delete c[act]; return c; });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (cat === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Beklenen Evrak Kataloğu</h1>
          <p className="text-sm text-slate-500 mt-1">Her faaliyet için beklenen evrak adlarını ve kabul edilen çalışma dosyası türlerini tanımlayın.</p>
        </div>
        <Button variant="outline" onClick={seed} data-testid="catalog-seed-btn"><Database className="h-4 w-4 mr-2" /> Alıcı Setini Yükle</Button>
      </div>

      {cat.map((a) => (
        <Card key={a.activity_code} className="border-slate-200" data-testid={`catalog-${a.activity_code}`}>
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="font-display text-base">{a.activity_label}</CardTitle>
            {a.defined ? <StatusBadge tone="active">Tanımlı</StatusBadge> : <StatusBadge tone="pending">Evrak listesi tanımlanmadı</StatusBadge>}
          </CardHeader>
          <CardContent>
            {!edit[a.activity_code] ? (
              <>
                {a.docs.length > 0 ? (
                  <ul className="text-sm text-slate-700 space-y-1 mb-3">
                    {a.docs.map((d) => <li key={d.key} className="flex justify-between"><span>{d.name}</span><span className="text-xs text-slate-400">{d.working_types.join(", ")}</span></li>)}
                  </ul>
                ) : <p className="text-sm text-slate-500 mb-3">Henüz evrak tanımlı değil.</p>}
                <Button variant="outline" size="sm" onClick={() => startEdit(a)} data-testid={`catalog-edit-${a.activity_code}`}>Düzenle</Button>
              </>
            ) : (
              <div className="space-y-2">
                {edit[a.activity_code].map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={d.name} onChange={(e) => setName(a.activity_code, i, e.target.value)} placeholder="Evrak adı" className="flex-1" data-testid={`catalog-doc-name-${a.activity_code}-${i}`} />
                    {TYPES.map((t) => (
                      <button key={t} onClick={() => toggleType(a.activity_code, i, t)} className={`text-xs px-2 py-1 rounded border ${d.working_types.includes(t) ? "bg-sky-50 border-sky-300 text-sky-700" : "border-slate-200 text-slate-500"}`}>{t}</button>
                    ))}
                    <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => rmDoc(a.activity_code, i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => addDoc(a.activity_code)}><Plus className="h-4 w-4 mr-1" /> Evrak Ekle</Button>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => save(a.activity_code)} data-testid={`catalog-save-${a.activity_code}`}><Save className="h-4 w-4 mr-1" /> Kaydet</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
